import { get, makeUrl } from '.';
import { requestStatus, updateRequestContext } from '.';


const ontvApi = '/api/integrations/ontv';

/**
 * Get tv guide(channels) resources for a specific lineup
 *
 * @param {string} lineup The channel lineup ID
 * @param {string} imageSize preferred image size
 * @param {string} startDate
 * @param endDate
 * @param {string} cacheMedia Whether the image should be cached and returned in `preferredImage.url` field
 * @param {object} context A context object with `progress`, `errors` and `busy` properties
 *
 * @returns {promise}
 */
export async function getChannels(lineup, imageSize = '', startDate = '', endDate = '', cacheMedia = true, context = null)
{
    const queries = { lineup, startDate, endDate, imageSize };

    if (cacheMedia) queries.cacheMedia = 1;

    const uri = makeUrl(`${ontvApi}/guide`, queries);

    if (context) updateRequestContext(context);

    return await get(uri)
        .then((response) => {
            const data = requestStatus(response, context);

            return data.guide ? data.guide : data;
        })
        .catch((error) => requestStatus({ errors: error }, context));
}

/**
 * Get a customer's lineup with its associated channels(call signs)
 *
 * @param {string} lineup Lineup UUID
 * @param {object} context A context object with `progress`, `errors` and `busy` properties
 *
 * @returns {promise}
 */
export async function getLineup(lineup, resolution = null, context = null)
{
    const queries = { lineup, resolution };
    const uri = makeUrl(`${ontvApi}/lineup`, queries);

    if (context) updateRequestContext(context);

    return await get(uri)
        .then((response) => {
            const data = requestStatus(response, context);

            return data;
        })
        .catch((error) => requestStatus({ errors: error }, context));
}

/**
 * Get appointments resources specified by the `uri`
 *
 * @param {string} lineup Lineup UUID
 * @param {string} media The filename
 * @param {string} width The requested image width
 * @param {string} height The requested image height
 * @param {object} context A context object with `progress`, `errors` and `busy` properties
 *
 * @returns {promise}
 */
export async function getMedia(lineup, media, width, height = '', context = null)
{
    const queries = { lineup, media, width };

    if (height) queries.height = height;

    const uri = makeUrl('/api/media/ontv-image', queries);

    if (context) updateRequestContext(context);

    return await get(uri)
        .then((response) => requestStatus(response, context))
        .catch((error) => requestStatus({ errors: error }, context));
}

/**
 * Returns detailed metadata for any program (Movie, Show, Episode, or Sports)
 *
 * @param {string} lineup Lineup UUID
 * @param {string} ontvId The movie|show|episode ID
 * @param {string} imageSize The program image size (Sm, Md, Lg, Ms)
 * @param {string} imageRatio Specify the program image aspect ratio (2x3, 3x4, 4x3, 16x9)
 * @param {string} cacheMedia Whether media/images should be cached and returned in `preferredImage.url` field
 * @param {object} context A context object with `progress`, `errors` and `busy` properties
 *
 * @returns {promise}
 */
export async function getPrograms(lineup, ontvId, imageSize = '', imageRatio ='', cacheMedia = true, context = null)
{
    const queries = { lineup, ontvId };

    if (imageSize) queries.imageSize = imageSize;

    if (imageRatio) queries.imageAspectTV = imageRatio;

    if (cacheMedia) queries.cacheMedia = 1;

    const uri = makeUrl(`${ontvApi}/programs`, queries);

    if (context) updateRequestContext(context);

    return await get(uri)
        .then((response) => requestStatus(response, context))
        .catch((error) => requestStatus({ errors: error }, context));
}
